import { DRAFT_STATES } from "@consts";
import { isNotCodefullAgent } from "@utils";

export const SYSTEM_MODE_KEY = "systemMode";
export const PREVIOUS_SYSTEM_MODE_KEY = "previousSystemMode";
export const SYSTEM_MODES = {
  READ_ONLY: "READ_ONLY",
  EDIT: "EDIT",
  PREVIEW: "PREVIEW"
};

const createAppSlice = (set, get) => ({
  httpReady: false,
  switchingTenant: false,
  systemMode: localStorage.getItem(SYSTEM_MODE_KEY) || SYSTEM_MODES.READ_ONLY,
  previousSystemMode: localStorage.getItem(PREVIOUS_SYSTEM_MODE_KEY) || "",
  openWidgetInitially: localStorage.getItem(SYSTEM_MODE_KEY) === SYSTEM_MODES.PREVIEW,
  isWebSnippetOpen: false,
  isDiscardingChanges: false,
  pageName: "",
  showHeaderSkeleton: true,
  showHeader: true,
  userData: {},
  userPermissions: {},
  selectedAccountId: null,
  selectedAssistant: null,
  isAssistantPreviewable: false,
  accounts: null,
  isLoggedInToLooker: false,
  additionalBreadcrumbs: [],
  couldNotSetUpAssistant: false,
  hasPageErrorType: null,
  setSwitchingTenant: (value) => set({ switchingTenant: value }),
  setDraft: (value) => set({ draft: value }),
  setPageError: (errorOrigin, error) => {
    const { pageName } = get();

    console.error(
      `${errorOrigin ?? "Error"}: \n`,
      `${error?.response?.statusText || error} \n`,
      `PageName: ${pageName} \n`,
      `URL: ${window.location?.href} \n`
    );

    set({ hasPageErrorType: error?.response?.status || 400 });
  },

  setHttpReady: (httpReady) => set({ httpReady }),
  setSystemMode: (newSystemMode) => {
    const {
      systemMode,
      updateConfigAndTriplets,
      closeSpeechPlayer
    } = get();
    closeSpeechPlayer();
    updateConfigAndTriplets(newSystemMode);
    localStorage.setItem("systemMode", newSystemMode);
    localStorage.setItem("previousSystemMode", systemMode);
    set({
      systemMode: newSystemMode,
      previousSystemMode: systemMode
    });
  },

  updateConfigAndTriplets: (newSystemMode) => {
    const {
      systemMode: storeSystemMode,
      currentTriplets,
      originalTriplets,
      collections,
      collectionConfig,
      selectedCollection,
      originalCollectionConfig,
      collectionSpec,
      validationResults,
      languages,
      draft,
      policiesData
    } = get();
    const isPreviewMode = newSystemMode === SYSTEM_MODES.PREVIEW || storeSystemMode === SYSTEM_MODES.PREVIEW;
    if (isPreviewMode) return;
    const isEditMode = (newSystemMode || storeSystemMode) === SYSTEM_MODES.EDIT;
    const updatedValidationResults = isEditMode && draft ? validationResults.draft : validationResults.live;
    const updatedCurrentSelectedConfig = isEditMode && draft ? collectionConfig : originalCollectionConfig;
    const updatedCurrentTriplets = isEditMode && draft && currentTriplets?.length ? currentTriplets : originalTriplets;
    const updatedCurrentCollections = isEditMode && draft ? { ...collections, currentConfig: collections?.configRef }
      : { ...(collections || {}), currentConfig: collections?.originalConfig };
    const updatedCurrentLanguages = isEditMode && draft ? languages.draft : languages.live;
    const updatedCurrentPolicies = isEditMode && draft ? policiesData?.policies?.draft : policiesData?.policies?.live;
    const updatedCurrentPoliciesSpec = isEditMode && draft ? policiesData?.spec?.draft : policiesData?.spec?.live;
    const updatedCurrentSelectedPolicy = isEditMode && draft ? policiesData?.selectedPolicy?.draft : policiesData?.selectedPolicy?.live;
    let policiesDataObj;
    if (policiesData) {
      policiesDataObj = {
        ...policiesData,
        policies: {
          ...policiesData?.policies,
          current: updatedCurrentPolicies,
          local: updatedCurrentPolicies
        },
        selectedPolicy: {
          ...policiesData?.selectedPolicy,
          current: updatedCurrentSelectedPolicy,
          local: updatedCurrentSelectedPolicy
        },
        spec: {
          ...policiesData?.spec,
          current: updatedCurrentPoliciesSpec
        }
      };
    }
    set({
      selectedCollection: selectedCollection?.key && updatedCurrentSelectedConfig ? {
        ...updatedCurrentSelectedConfig,
        spec: collectionSpec
      } : null,
      collections: updatedCurrentCollections,
      displayedTriplets: updatedCurrentTriplets,
      languages: {
        ...languages,
        current: updatedCurrentLanguages
      },
      validationResults: {
        ...validationResults,
        current: updatedValidationResults
      },
      policiesData: policiesDataObj
    });
  },

  setIsWebSnippetOpen: isWebSnippetOpen => set({ isWebSnippetOpen }),
  setPageName: value => set({ pageName: value }),
  setShowHeaderSkeleton: value => set({ showHeaderSkeleton: value }),
  setShowHeader: value => set({ showHeader: value }),

  isReadOnlyMode: () => {
    const { draft, isDraftOwner } = get();
    return (
      draft?.status === DRAFT_STATES.READ_ONLY
      || (draft?.userEmail && !isDraftOwner())
    );
  },

  isDraftOwner: () => {
    const { userData, draft } = get();
    return draft?.userEmail === userData?.email;
  },

  setIsLoggedInToLooker: (isLoggedIn) => {
    set({ isLoggedInToLooker: isLoggedIn });
  },

  setDashboardSettings: (dashboardSettings) => {
    set({ dashboardSettings });
  },

  setSelectedAccountId: (selectedAccountId) => {
    set({ selectedAccountId });
  },

  setupAccounts: (accounts) => {
    set({ accounts });
  },

  setPermissions: (userData) => {
    set({
      userData,
      userPermissions: {
        permissions: userData.permissions.map(permission => permission.key),
        userId: userData.id
      }
    });
  },

  setUserData: (userData) => {
    set({ userData });
  },

  clearAccountAndAssistant: () => {
    set({
      hasPageErrorType: null,
      selectedAccountId: null,
      selectedAssistant: null,
      assistants: [],
      isLoggedInToLooker: false
    });
  },

  clearAgent: () => {
    set({
      selectedAssistant: null,
      isLoggedInToLooker: false,
      hasPageErrorType: null
    });
  },

  setNonCodelessData: async () => {
    const { initTriplets, getKnowledgeGraph } = get();

    await initTriplets();
    getKnowledgeGraph();
  },

  setSelectedAssistant: (assistant) => {
    set({
      hasPageErrorType: null,
      isLoggedInToLooker: false,
      selectedAssistant: assistant
    });

    const {
      clearTriplets,
      resetConversationsState
    } = get();

    clearTriplets();
    resetConversationsState();
  },

  hasPermission: (permission) => {
    const { userPermissions } = get();
    return userPermissions?.permissions?.includes(permission);
  },
  isCodeless: () => {
    const { selectedAssistant } = get();
    return isNotCodefullAgent(selectedAssistant?.assistantId);
  }
});

export default createAppSlice;
